import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";

import { useBillingPrices, useCreateStripeCheckoutSession } from "@shared/queryHooks/useBilling";
import { useToastContext } from "@shared/context/ToastContext";

type Props = {
  onCancel: () => void;
  purpose: "publishJob" | "addTeamMember";
  jobId?: number;
  location: any;
};

function StartFreeTrialModal(props: Props) {
  const { jobId, location, purpose } = props;
  const addToast = useToastContext();
  const [selectedPeriod, setSelectedPeriod] = useState<"monthly" | "yearly">("monthly");

  const {
    data: billingPricesData,
    isLoading: isLoadingBillingPrices,
  } = useBillingPrices({
    refetchOnWindowFocus: false,
  });
  const billingPrices = billingPricesData != undefined ? billingPricesData.data : [];
  const starterV2Prices = billingPrices
    .filter((price) => price.lookupKey?.includes("starter_v2"))
    .sort((a, b) => a.unitAmount - b.unitAmount);

  const monthlyPrice = starterV2Prices.find((p) => p.recurring?.interval === "month");
  const yearlyPrice = starterV2Prices.find((p) => p.recurring?.interval === "year");
  const selectedPrice = selectedPeriod === "monthly" ? monthlyPrice : yearlyPrice;

  const monthlyDisplayPrice = monthlyPrice ? monthlyPrice.unitAmount / 100 : null;
  const yearlyDisplayPrice = yearlyPrice ? Math.round(yearlyPrice.unitAmount / 12 / 100) : null;

  const {
    mutate: createStripeCheckoutSession,
    isLoading: isLoadingCreateCheckoutSession,
  } = useCreateStripeCheckoutSession();

  window.logger("%c[StartFreeTrialModal] ", "background-color: #7fa7cf", {
    props,
    location,
    jobId,
    billingPrices,
    starterV2Prices,
    monthlyPrice,
    yearlyPrice,
    selectedPrice,
    selectedPeriod,
  });

  const createCheckoutSession = ({ priceId, planType, planPeriod }) => {
    const successUrl = jobId
      ? `/jobs/${jobId}/stripe_checkout_redirect_handler`
      : location.pathname;
    const cancelUrl = jobId
      ? `/jobs/${jobId}/stripe_checkout_redirect_handler`
      : location.pathname;

    createStripeCheckoutSession(
      {
        priceId,
        successUrl,
        cancelUrl,
        locationPathname: location.pathname,
        ...(jobId ? { jobId } : {}),
        planType,
        planPeriod,
      },
      {
        onError: (error: any) => {
          window.logger(
            "%c[StartFreeTrialModal] onError createStripeCheckoutSession",
            "background: orange; color: #1976D2",
            error,
          );
          addToast({
            title: "Could not start checkout. Please contact support@polymer.co",
            kind: "warning",
          });
          props.onCancel();
        },
        onSuccess: (data) => {
          window.logger(
            "%c[StartFreeTrialModal] completed creation of Checkout Session",
            "background: orange; color: #1976D2",
            data,
          );
          (window as any).STRIPE.redirectToCheckout({
            sessionId: data.id,
          }).then(function (result) {
            window.logger(
              "%c[StartFreeTrialModal] REDIRECTING",
              "background: green; color: white",
              result,
            );
            props.onCancel();
          });
        },
      },
    );
  };

  const handleStartTrial = () => {
    if (!selectedPrice) return;
    createCheckoutSession({
      priceId: selectedPrice.id,
      planType: "limited",
      planPeriod: selectedPeriod,
    });
  };

  return (
    <CenterModal headerTitleText="Start your free trial" onCancel={props.onCancel}>
      <>
        <Styled.Instructions>
          Start your <b>14-day free trial</b> to{" "}
          {purpose === "publishJob" ? "publish your job" : "add a team member"}.
          You won't be charged until the trial ends.
          <br />
          <br />
          Begin with the Starter Plan or view more options in{" "}
          <Link to="/hire/settings/billing" onClick={props.onCancel}>
            plan and billing
          </Link>
          .
        </Styled.Instructions>

        <Styled.PlanCards>
          <Styled.PlanCard
            type="button"
            isSelected={selectedPeriod === "monthly"}
            onClick={() => setSelectedPeriod("monthly")}
          >
            <Styled.PlanCardName>
              <h3>
                Starter <span>Monthly</span>
              </h3>
            </Styled.PlanCardName>
            <Styled.PlanCardPrice>
              <h4>
                ${monthlyDisplayPrice || <Styled.PriceSkeleton />}
                <span>
                  per month <br />
                  billed monthly
                </span>
              </h4>
            </Styled.PlanCardPrice>
            <Styled.Features>
              <Styled.Feature>
                <Icon name="check-circle" />
                <Styled.FeatureContent>
                  <Styled.FeatureName>5 jobs</Styled.FeatureName>
                </Styled.FeatureContent>
              </Styled.Feature>
              <Styled.Feature>
                <Icon name="check-circle" />
                <Styled.FeatureContent>
                  <Styled.FeatureName>Unlimited users</Styled.FeatureName>
                </Styled.FeatureContent>
              </Styled.Feature>
            </Styled.Features>
          </Styled.PlanCard>

          <Styled.PlanCard
            type="button"
            isSelected={selectedPeriod === "yearly"}
            onClick={() => setSelectedPeriod("yearly")}
          >
            <Styled.PlanCardName>
              <h3>
                Starter <span>Yearly</span>
              </h3>
              <Styled.Badge>2 months free</Styled.Badge>
            </Styled.PlanCardName>
            <Styled.PlanCardPrice>
              <h4>
                ${yearlyDisplayPrice || <Styled.PriceSkeleton />}
                <span>
                  per month <br />
                  billed yearly
                </span>
              </h4>
            </Styled.PlanCardPrice>
            <Styled.Features>
              <Styled.Feature>
                <Icon name="check-circle" />
                <Styled.FeatureContent>
                  <Styled.FeatureName>5 jobs</Styled.FeatureName>
                </Styled.FeatureContent>
              </Styled.Feature>
              <Styled.Feature>
                <Icon name="check-circle" />
                <Styled.FeatureContent>
                  <Styled.FeatureName>Unlimited users</Styled.FeatureName>
                </Styled.FeatureContent>
              </Styled.Feature>
            </Styled.Features>
          </Styled.PlanCard>
        </Styled.PlanCards>

        <Styled.ButtonContainer>
          <Button
            name="startTrialButton"
            type="submit"
            onClick={handleStartTrial}
            loading={isLoadingCreateCheckoutSession}
            disabled={isLoadingBillingPrices || isLoadingCreateCheckoutSession}
          >
            Start free trial now
          </Button>
          <Button styleType="secondary" onClick={props.onCancel}>
            Cancel
          </Button>
        </Styled.ButtonContainer>
      </>
    </CenterModal>
  );
}

export default StartFreeTrialModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Instructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_Instructions;
    ${[t.mb(5)]}
  `;
});

Styled.PlanCards = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_PlanCards;
    display: flex;
    gap: 12px;
    ${[t.mb(9)]}
  `;
});

Styled.PlanCard = styled.button<{ isSelected: boolean }>((props) => {
  const t: any = props.theme;
  const { isSelected } = props;
  return css`
    label: StartFreeTrialModal_PlanCard;
    flex: 1;
    padding: 16px;
    border-radius: 8px;
    border: 2px solid ${
      isSelected
        ? t.dark
          ? t.color.gray[300]
          : t.color.black
        : t.dark
        ? t.color.gray[600]
        : t.color.gray[200]
    };
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    outline: none;

    &:hover,
    &:focus-visible {
      border-color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
    }

    &:focus-visible {
      ${t.dark ? "" : `box-shadow: 0 0 0 2px ${t.color.gray[300]};`}
    }
  `;
});

Styled.PlanCardName = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_PlanCardName;
    display: flex;
    ${[t.mb(4)]}
    align-items: center;
    flex-direction: row;

    h3 {
      ${[t.text.bold, t.h(6)]}
      font-size: 17px;
      line-height: 1.6;
      &:first-letter {
        text-transform: uppercase;
      }

      span {
        ${t.text.normal}
        color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
      }
    }
  `;
});

Styled.Features = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_Features;
    ${t.mb(4)}
  `;
});

Styled.Feature = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_Feature;
    ${[t.mb(3)]}
    display: flex;
    align-items: flex-start;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};

    svg {
      ${[t.mr(2)]}
      flex-shrink: 0;
      stroke: ${t.dark ? t.color.gray[200] : t.color.gray[500]};
      width: 14px;
      height: 14px;
      margin-top: 1px;
    }
  `;
});

Styled.FeatureContent = styled.div(() => {
  return css`
    label: StartFreeTrialModal_FeatureContent;
    display: flex;
    flex-direction: column;
  `;
});

Styled.FeatureName = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_FeatureName;
    font-size: 13px;
    line-height: 1.4;
  `;
});

Styled.PriceSkeleton = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_PriceSkeleton;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 30px;
    border-radius: 8px;
    margin-left: 2px;
    background: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
  `;
});

Styled.Badge = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_Badge;
    ${[t.h(3), t.ml(2), t.mt(1), t.px(2)]};
    font-size: 0.55rem;
    color: ${t.color.black};
    background: linear-gradient(84.47deg, #fbd7ff 0%, #ffe4cc 100%, #ffdec1 100%);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    line-height: 1.3;
    height: 19px;
    border-radius: 5px;
    font-weight: 500;
  `;
});

Styled.PlanCardPrice = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_PlanCardPrice;
    ${[t.mb(5)]}
    display: flex;
    align-items: center;
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    h4 {
      ${[t.text.bold]}
      font-size: 2rem;
    }

    span {
      display: inline-block;
      ${[t.text.normal, t.text.xs, t.ml(1)]}
      line-height: 1.4;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: StartFreeTrialModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});
